import type {
  MeasurementName,
  ObservationVitalMeasurement,
} from '@quromedical/models';
import {
  type MeasurePartsConfig,
  type MeasurePartsInputType,
  measureParts,
  titles,
} from 'core/vitals';
import { Dialog } from 'design-system/components';
import type React from 'react';
import { useMemo } from 'react';
import { strings } from 'strings';

import type { VitalObservationForm } from './types';
import { VitalObservationMultipleCreateForm } from './VitalObservationMultipleCreateForm';
import { VitalObservationSingleCreateForm } from './VitalObservationSingleCreateForm';

interface VitalObservationCreateFormProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * The default measurement
   */
  measure: MeasurementName;

  onSubmit: (value: ObservationVitalMeasurement[]) => Promise<void>;
}

const createTitle = (measure: MeasurementName): string =>
  `${strings.VitalObservationFormTitlePrefix} ${titles[measure] || ''} ${
    strings.VitalObservationFormTitleSuffix
  }`;

const Forms: Record<MeasurePartsInputType, VitalObservationForm> = {
  multiple: VitalObservationMultipleCreateForm,
  single: VitalObservationSingleCreateForm,
};

export const VitalObservationCreateForm: React.FC<
  VitalObservationCreateFormProps
> = ({ measure, onSubmit, isOpen, onClose }) => {
  const measureConfig = useMemo<MeasurePartsConfig>(
    () =>
      measureParts[measure] || {
        measurementNames: [measure],
        inputType: 'single',
      },
    [measure],
  );

  const title = createTitle(measure);

  const Form = Forms[measureConfig.inputType];

  return (
    <Dialog isOpen={isOpen} onRequestClose={onClose} title={title}>
      <Form
        onSubmit={onSubmit}
        onCancel={onClose}
        measurementNames={measureConfig.measurementNames}
      />
    </Dialog>
  );
};
